import { Avatar, AvatarGroup, Text } from '@components/base/display';
import { VStack, HStack } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import { SizeVariantsGrid } from '../SizeVariantsGrid';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

const sizes = ['xs', 'sm', 'md', 'lg', 'xl'] as const;
const statuses = ['online', 'offline', 'busy', 'away'] as const;

const sizeDescriptions: Record<(typeof sizes)[number], string> = {
  xs: '24px - Compact displays',
  sm: '32px - Small displays',
  md: '40px - Standard (default)',
  lg: '48px - Larger displays',
  xl: '64px - Prominent displays',
};

const statusDescriptions: Record<(typeof statuses)[number], string> = {
  online: 'Active/Available',
  offline: 'Inactive/Unavailable',
  busy: 'Occupied/Do not disturb',
  away: 'Temporarily away',
};

// Sample user data for examples
const sampleUsers = [
  { name: 'Alice Johnson', src: 'https://i.pravatar.cc/150?u=alice' },
  { name: 'Bob Smith', src: 'https://i.pravatar.cc/150?u=bob' },
  { name: 'Carol Davis', src: 'https://i.pravatar.cc/150?u=carol' },
  { name: 'David Wilson', src: 'https://i.pravatar.cc/150?u=david' },
  { name: 'Emma Brown', src: 'https://i.pravatar.cc/150?u=emma' },
  { name: 'Frank Miller', src: 'https://i.pravatar.cc/150?u=frank' },
];

function AvatarShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  // Map 'none' to undefined for status prop
  const statusValue = playgroundProps.status === 'none' ? undefined : (playgroundProps.status as 'online' | 'offline' | 'busy' | 'away');

  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="avatar">
          <Avatar
            name="Jane Smith"
            src="https://i.pravatar.cc/150?u=jane"
            size={playgroundProps.size as 'xs' | 'sm' | 'md' | 'lg' | 'xl'}
            status={statusValue}
          />
      </ShowcasePreviewSection>

      {/* Size Variants */}
      <ShowcaseDemoSection
        title="Size Variants"
        description="Avatars come in 5 sizes for different contexts"
       
      >
          <SizeVariantsGrid
            entries={sizes.map((size) => ({
              size,
              dimension: sizeDescriptions[size].split(' - ')[0]!,
              description: sizeDescriptions[size].split(' - ')[1]!,
              renderComponent: () => <Avatar name="Jane Doe" size={size} />,
            }))}
          />
      </ShowcaseDemoSection>

      {/* Image vs Initials */}
      <ShowcaseDemoSection
        title="Image vs Initials Fallback"
        description="Shows image when available, falls back to initials derived from name"
       
      >
          <HStack gap={6}>
            <VStack gap={2} align="center">
              <Avatar
                name="Sarah Connor"
                src="https://i.pravatar.cc/150?u=sarah"
                size="lg"
              />
              <Text size="xs" color="muted">
                With image
              </Text>
            </VStack>
            <VStack gap={2} align="center">
              <Avatar name="Sarah Connor" size="lg" />
              <Text size="xs" color="muted">
                Initials fallback
              </Text>
            </VStack>
            <VStack gap={2} align="center">
              <Avatar name="John" size="lg" />
              <Text size="xs" color="muted">
                Single name
              </Text>
            </VStack>
            <VStack gap={2} align="center">
              <Avatar size="lg" />
              <Text size="xs" color="muted">
                No name (?)
              </Text>
            </VStack>
          </HStack>
      </ShowcaseDemoSection>

      {/* Status Indicators */}
      <ShowcaseDemoSection
        title="Status Indicators"
        description="Colored dots indicate user availability status"
       
      >
          <VStack gap={4}>
            {statuses.map((status) => (
              <HStack key={status} gap={4} align="center">
                <Text size="sm" color="muted" className="w-20 flex-shrink-0">
                  {status}
                </Text>
                <Avatar name="User Name" status={status} />
                <Text size="sm" color="secondary">
                  {statusDescriptions[status]}
                </Text>
              </HStack>
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* Bordered Avatars */}
      <ShowcaseDemoSection
        title="Bordered Avatars"
        description="White ring border for stacking or overlay contexts"
       
      >
          <HStack gap={4}>
            <VStack gap={2} align="center">
              <Avatar name="Alice" size="lg" bordered />
              <Text size="xs" color="muted">
                bordered
              </Text>
            </VStack>
            <VStack gap={2} align="center">
              <Avatar name="Bob" size="lg" />
              <Text size="xs" color="muted">
                no border
              </Text>
            </VStack>
          </HStack>
      </ShowcaseDemoSection>

      {/* Avatar Group */}
      <ShowcaseDemoSection
        title="Avatar Group"
        description="Display multiple avatars in a stacked row with optional max count"
       
      >
          <VStack gap={6}>
            {/* Basic group */}
            <VStack gap={2}>
              <Text size="sm" weight="medium">
                Basic group
              </Text>
              <AvatarGroup>
                {sampleUsers.slice(0, 3).map((user) => (
                  <Avatar key={user.name} name={user.name} src={user.src} />
                ))}
              </AvatarGroup>
            </VStack>

            {/* With max limit */}
            <VStack gap={2}>
              <Text size="sm" weight="medium">
                With max limit (max=3)
              </Text>
              <AvatarGroup max={3}>
                {sampleUsers.map((user) => (
                  <Avatar key={user.name} name={user.name} src={user.src} />
                ))}
              </AvatarGroup>
              <Text size="xs" color="muted">
                Shows +3 indicator for remaining avatars
              </Text>
            </VStack>

            {/* Different sizes */}
            <VStack gap={2}>
              <Text size="sm" weight="medium">
                Different sizes
              </Text>
              <HStack gap={6}>
                <VStack gap={1}>
                  <AvatarGroup size="sm" max={4}>
                    {sampleUsers.map((user) => (
                      <Avatar key={user.name} name={user.name} />
                    ))}
                  </AvatarGroup>
                  <Text size="xs" color="muted">
                    sm
                  </Text>
                </VStack>
                <VStack gap={1}>
                  <AvatarGroup size="md" max={4}>
                    {sampleUsers.map((user) => (
                      <Avatar key={user.name} name={user.name} />
                    ))}
                  </AvatarGroup>
                  <Text size="xs" color="muted">
                    md
                  </Text>
                </VStack>
                <VStack gap={1}>
                  <AvatarGroup size="lg" max={4}>
                    {sampleUsers.map((user) => (
                      <Avatar key={user.name} name={user.name} />
                    ))}
                  </AvatarGroup>
                  <Text size="xs" color="muted">
                    lg
                  </Text>
                </VStack>
              </HStack>
            </VStack>
          </VStack>
      </ShowcaseDemoSection>

      {/* Insurance Domain Examples */}
      <ShowcaseDemoSection
        title="Insurance Domain Examples"
        description="Common avatar usage in insurance applications"
       
      >
          <VStack gap={4}>
            {/* Agent assignment */}
            <VStack gap={2}>
              <Text size="sm" weight="medium">
                Agent Assignment
              </Text>
              <HStack gap={3} align="center">
                <Avatar
                  name="Michael Chen"
                  src="https://i.pravatar.cc/150?u=michael"
                  status="online"
                />
                <VStack gap={0}>
                  <Text weight="medium">Michael Chen</Text>
                  <Text size="sm" color="muted">
                    Senior Claims Adjuster
                  </Text>
                </VStack>
              </HStack>
            </VStack>

            {/* Policy stakeholders */}
            <VStack gap={2}>
              <Text size="sm" weight="medium">
                Policy Stakeholders
              </Text>
              <HStack gap={4}>
                <VStack gap={1} align="center">
                  <Avatar name="John Doe" size="md" />
                  <Text size="xs">Policyholder</Text>
                </VStack>
                <VStack gap={1} align="center">
                  <Avatar name="Jane Doe" size="md" />
                  <Text size="xs">Co-insured</Text>
                </VStack>
                <VStack gap={1} align="center">
                  <Avatar name="Robert Doe" size="md" />
                  <Text size="xs">Beneficiary</Text>
                </VStack>
              </HStack>
            </VStack>

            {/* Team collaboration */}
            <VStack gap={2}>
              <Text size="sm" weight="medium">
                Claim Review Team
              </Text>
              <HStack gap={3} align="center">
                <AvatarGroup max={4} size="sm">
                  <Avatar name="Reviewer 1" status="online" />
                  <Avatar name="Reviewer 2" status="online" />
                  <Avatar name="Reviewer 3" status="busy" />
                  <Avatar name="Manager" status="away" />
                  <Avatar name="Specialist" status="offline" />
                </AvatarGroup>
                <Text size="sm" color="muted">
                  5 team members assigned
                </Text>
              </HStack>
            </VStack>
          </VStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const AvatarShowcase: ShowcaseDefinition = {
  id: 'avatar',
  name: 'Avatar',
  category: 'display',
  description: 'User or entity image display with fallback initials',
  propsDefinition: [
    {
      name: 'size',
      type: 'select',
      options: ['xs', 'sm', 'md', 'lg', 'xl'],
      defaultValue: 'md',
      description: 'Size of the avatar (xs=24px, sm=32px, md=40px, lg=48px, xl=64px)',
    },
    {
      name: 'status',
      type: 'select',
      options: ['none', 'online', 'offline', 'busy', 'away'],
      defaultValue: 'none',
      description: 'Status indicator dot (none = no indicator)',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'role="img" on the avatar container',
      'aria-label derived from name prop',
      'Status indicator has aria-label describing the status',
    ],
    keyboardNav: [],
    notes: [
      'Always provide the name prop for proper accessibility',
      'Alt text for images falls back to name if not provided',
      'AvatarGroup has role="group" with aria-label',
    ],
  },
  component: AvatarShowcaseComponent,
};
